// The one box state machine (03 §3.6). The table lives in box-transitions.ts; this file applies it.
// Physical scans are facts: when they do not fit the table the box follows them and an exception
// is returned. Business rules (payment, holds, cancellation) refuse instead.

import {
  ADMINISTRATIVE_EVENTS,
  findTransition,
  legalEventTypes,
  refusalPreconditionsFor,
  targetStatus,
} from './box-transitions';
import type { RefusalPrecondition, TransitionRow } from './box-transitions';
import { BRANCH_RECEIVING_EVENTS, NO_FLAGS, hasActiveHold, hasBlockingHold } from './box-types';
import type {
  Box,
  BoxContext,
  BoxEvent,
  BoxEventType,
  BoxException,
  BoxFlags,
  BoxLocation,
  BoxOutcome,
  BoxPartner,
  BoxRefusalReason,
  CollectEvent,
  Hold,
  LastEvent,
} from './box-types';
import { parseInstant } from './time';

export * from './box-types';
export * from './box-transitions';

// ------------------------------------------------------------ event keys

/** The carrier an event is about: the id a repeat scan must share to count as a duplicate. */
export function eventCarrierId(event: BoxEvent): string {
  switch (event.type) {
    case 'collect':
      return event.shipmentId;
    case 'loadTrip':
    case 'unloadFromTrip':
    case 'receiveTrip':
      return event.tripId;
    case 'loadContainer':
    case 'removeFromContainer':
    case 'exportContainer':
    case 'containerArrived':
    case 'receiveContainer':
      return event.containerId;
    case 'handoverToPartner':
    case 'partnerUpdate':
    case 'returnFromPartner':
      return event.handoverId;
    case 'startRun':
    case 'deliverStop':
    case 'closeRun':
      return event.runId;
    case 'completeReturn':
      return event.branchId;
    case 'cancelShipment':
      return '';
    case 'writeOff':
    case 'found':
      return event.exceptionId;
  }
}

/** Same type and carrier means duplicate. A partner update also carries its status: each one is news. */
export function eventDedupeKey(event: BoxEvent): string {
  const key = `${event.type}:${eventCarrierId(event)}`;
  return event.type === 'partnerUpdate' ? `${key}:${event.partnerStatus}` : key;
}

/** The carrier the box is currently on, for events that must continue from it. */
function boxCarrierId(box: Box, event: BoxEvent): string | undefined {
  switch (event.type) {
    case 'unloadFromTrip':
    case 'receiveTrip':
      return box.tripId;
    case 'removeFromContainer':
    case 'exportContainer':
    case 'containerArrived':
    case 'receiveContainer':
      return box.containerId;
    case 'partnerUpdate':
    case 'returnFromPartner':
      return box.partner?.handoverId;
    case 'deliverStop':
    case 'closeRun':
      return box.runId;
    default:
      return undefined;
  }
}

// -------------------------------------------------------------- refusals

const REFUSAL_RULES: Record<
  RefusalPrecondition,
  { reason: BoxRefusalReason; satisfied: (box: Box, ctx: BoxContext) => boolean }
> = {
  // D-16: nothing ships or is handed over until paid in full.
  paid_in_full: { reason: 'unpaid', satisfied: (_box, ctx) => ctx.shipmentPaidInFull === true },
  no_active_hold: { reason: 'active_hold', satisfied: (box) => !hasActiveHold(box) },
  no_blocking_hold: { reason: 'blocking_hold', satisfied: (box) => !hasBlockingHold(box) },
  returning_flag: { reason: 'not_returning', satisfied: (box) => box.flags.returning },
  shipment_not_exported: {
    reason: 'shipment_exported',
    satisfied: (_box, ctx) => ctx.anyBoxExported !== true,
  },
};

function refusalFor(box: Box, type: BoxEventType, ctx: BoxContext): BoxRefusalReason | undefined {
  const failed = refusalPreconditionsFor(type).find(
    (rule) => !REFUSAL_RULES[rule].satisfied(box, ctx),
  );
  return failed && REFUSAL_RULES[failed].reason;
}

/** The business rule that would refuse this event on this box now, or undefined (screens warn early with it). */
export function boxRefusal(
  box: Box,
  type: BoxEventType,
  ctx: BoxContext = {},
): BoxRefusalReason | undefined {
  return refusalFor(box, type, ctx);
}

// ------------------------------------------------------ physical checks

function physicalProblem(box: Box, event: BoxEvent, row: TransitionRow): BoxException | undefined {
  for (const precondition of row.preconditions) {
    if (precondition === 'at_event_branch' && box.currentBranchId !== event.branchId) {
      return {
        type: 'illegal_scan',
        reason: `${event.type} scanned at ${event.branchId} but the box was last received at ${box.currentBranchId}`,
      };
    }
    if (precondition === 'same_carrier') {
      const expected = boxCarrierId(box, event);
      if (expected !== undefined && expected !== eventCarrierId(event)) {
        return {
          type: 'illegal_scan',
          reason: `${event.type} on ${eventCarrierId(event)} but the box is on ${expected}`,
        };
      }
    }
    if (precondition === 'scan_at_destination') {
      const expected =
        event.type === 'receiveTrip'
          ? event.toBranchId
          : event.type === 'receiveContainer'
            ? event.importHubId
            : undefined;
      if (expected !== undefined && expected !== event.branchId) {
        return {
          type: 'wrong_destination',
          reason: `${event.type} scanned at ${event.branchId} but the destination is ${expected}`,
        };
      }
    }
  }
  return undefined;
}

// -------------------------------------------------------------- effects

function branchLocation(id: string): BoxLocation {
  return { kind: 'branch', id };
}

function mergeRefs(existing: readonly string[], added: readonly string[] = []): readonly string[] {
  return [...new Set([...existing, ...added])];
}

interface Placement {
  readonly location: BoxLocation;
  readonly tripId?: string;
  readonly containerId?: string;
  readonly runId?: string;
  readonly partner?: BoxPartner;
}

/** Where the box is after the event. Carrier ids are only set while the box is on that carrier. */
function placementAfter(box: Box, event: BoxEvent): Placement {
  switch (event.type) {
    case 'collect':
    case 'unloadFromTrip':
    case 'receiveTrip':
    case 'removeFromContainer':
    case 'receiveContainer':
    case 'returnFromPartner':
    case 'closeRun':
    case 'completeReturn':
    case 'found':
      return { location: branchLocation(event.branchId) };
    case 'loadTrip':
      return { location: { kind: 'trip', id: event.tripId }, tripId: event.tripId };
    case 'loadContainer':
    case 'exportContainer':
    case 'containerArrived':
      return {
        location: { kind: 'container', id: event.containerId },
        containerId: event.containerId,
      };
    case 'handoverToPartner':
      return {
        location: { kind: 'partner', id: event.partnerId },
        partner: {
          partnerId: event.partnerId,
          handoverId: event.handoverId,
          partnerStatus: 'received_by_partner',
          partnerRefs: mergeRefs([], event.partnerRefs),
          lastUpdateAt: event.occurredAt,
        },
      };
    case 'partnerUpdate': {
      const sameHandover = box.partner?.handoverId === event.handoverId;
      const partner: BoxPartner = {
        partnerId: event.partnerId,
        handoverId: event.handoverId,
        partnerStatus: event.partnerStatus,
        partnerRefs: mergeRefs(
          sameHandover ? (box.partner?.partnerRefs ?? []) : [],
          event.partnerRefs,
        ),
        lastUpdateAt: event.occurredAt,
      };
      return event.partnerStatus === 'delivered'
        ? { location: { kind: 'consignee', id: box.shipmentId }, partner }
        : { location: { kind: 'partner', id: event.partnerId }, partner };
    }
    case 'startRun':
      return { location: { kind: 'run', id: event.runId }, runId: event.runId };
    case 'deliverStop':
      return { location: { kind: 'consignee', id: box.shipmentId } };
    case 'cancelShipment':
      return { location: box.location };
    case 'writeOff':
      return { location: { kind: 'unknown', id: '' } };
  }
}

function flagsAfter(box: Box, event: BoxEvent): BoxFlags {
  if (event.type === 'writeOff') return { ...box.flags, unaccounted: true };
  if (event.type === 'found') return { ...box.flags, unaccounted: false };
  if (event.type !== 'partnerUpdate') return box.flags;
  if (event.partnerStatus === 'damaged') return { ...box.flags, damaged: true };
  if (event.partnerStatus === 'lost') return { ...box.flags, unaccounted: true };
  return box.flags;
}

function lastEventOf(event: BoxEvent): LastEvent {
  return {
    type: event.type,
    at: event.occurredAt,
    branchId: event.branchId,
    ...(event.by !== undefined && { by: event.by }),
    ...(event.deviceId !== undefined && { deviceId: event.deviceId }),
    dedupeKey: eventDedupeKey(event),
  };
}

function moveBox(box: Box, event: BoxEvent): Box {
  const { tripId: _trip, containerId: _container, runId: _run, partner: _partner, ...kept } = box;
  return {
    ...kept,
    status: targetStatus(event),
    // 03 §3.4: only a receive (or the first collect) changes the box's branch.
    currentBranchId: BRANCH_RECEIVING_EVENTS.includes(event.type)
      ? event.branchId
      : box.currentBranchId,
    ...placementAfter(box, event),
    flags: flagsAfter(box, event),
    lastEvent: lastEventOf(event),
    version: box.version + 1,
  };
}

function createBox(event: CollectEvent): Box {
  return {
    barcode: event.barcode,
    shipmentId: event.shipmentId,
    status: 'at_branch',
    location: branchLocation(event.branchId),
    currentBranchId: event.branchId,
    holds: [],
    flags: NO_FLAGS,
    lastEvent: lastEventOf(event),
    version: 1,
  };
}

// ---------------------------------------------------------------- reducer

/**
 * Applies one scan to a box (`undefined` before the first collect).
 *
 * - `refused`: a business rule says no. Nothing changes.
 * - `duplicate`: the same event repeated on the same carrier. Box unchanged, no exception.
 * - `exception`: the scan does not fit the table. The box follows the scan.
 * - `ok`: a legal transition.
 */
export function applyBoxEvent(
  box: Box | undefined,
  event: BoxEvent,
  ctx: BoxContext = {},
): BoxOutcome {
  if (box === undefined) {
    return event.type === 'collect'
      ? { result: 'ok', box: createBox(event) }
      : { result: 'refused', reason: 'unknown_box' };
  }
  if (event.type === 'collect') {
    // A barcode attaches to one shipment only (03 §3.4 Barcode).
    return box.shipmentId === event.shipmentId
      ? { result: 'duplicate', box }
      : { result: 'refused', reason: 'barcode_already_attached' };
  }
  if (box.lastEvent?.dedupeKey === eventDedupeKey(event)) return { result: 'duplicate', box };

  const refusal = refusalFor(box, event.type, ctx);
  if (refusal !== undefined) return { result: 'refused', reason: refusal };

  const row = findTransition(box.status, event);
  if (row === undefined && ADMINISTRATIVE_EVENTS.includes(event.type)) {
    return { result: 'refused', reason: 'illegal_state' };
  }

  const next = moveBox(box, event);
  const exception: BoxException | undefined =
    row === undefined
      ? { type: 'illegal_scan', reason: `${event.type} is not legal from ${box.status}` }
      : (physicalProblem(box, event, row) ??
        (row.raises && {
          type: row.raises,
          reason: `partner reported ${row.partnerStatuses?.join('/') ?? row.raises}`,
        }));
  return exception ? { result: 'exception', box: next, exception } : { result: 'ok', box: next };
}

/** Event types a UI may offer for this box. Pass `ctx` to also apply payment and cancellation gates. */
export function allowedEvents(box: Box | undefined, ctx?: BoxContext): BoxEventType[] {
  if (box === undefined) return legalEventTypes('none');
  const assumed: BoxContext = ctx ?? { shipmentPaidInFull: true, anyBoxExported: false };
  return legalEventTypes(box.status).filter((type) => refusalFor(box, type, assumed) === undefined);
}

// ----------------------------------------------------------------- replay

/** Replaying history that was already accepted: the payment and export gates passed at the time. */
export const REPLAY_CONTEXT: BoxContext = { shipmentPaidInFull: true, anyBoxExported: false };

export interface ReplayResult {
  readonly box: Box | undefined;
  /** Outcomes in the order the events were applied (occurredAt order). */
  readonly outcomes: readonly { readonly event: BoxEvent; readonly outcome: BoxOutcome }[];
}

/**
 * Folds events in `occurredAt` order (ties keep input order), so a late-arriving offline scan
 * lands where it happened, not where it synced (03 §3.4 box events).
 */
export function replayBoxEvents(
  events: readonly BoxEvent[],
  options: { readonly initial?: Box; readonly ctx?: BoxContext } = {},
): ReplayResult {
  const ordered = events
    .map((event, index) => ({ event, index, at: parseInstant(event.occurredAt) }))
    .sort((a, b) => a.at - b.at || a.index - b.index);

  let box = options.initial;
  const outcomes: { event: BoxEvent; outcome: BoxOutcome }[] = [];
  for (const { event } of ordered) {
    const outcome = applyBoxEvent(box, event, options.ctx ?? REPLAY_CONTEXT);
    if (outcome.result !== 'refused') box = outcome.box;
    outcomes.push({ event, outcome });
  }
  return { box, outcomes };
}

// ------------------------------------------------- flags and holds (no scan)

/**
 * Sets or clears a flag without moving the box: a missing box stays where it was last seen
 * (`unaccounted`), a damaged one stays where it is.
 */
export function setBoxFlag(box: Box, flag: keyof BoxFlags, value: boolean): Box {
  return { ...box, flags: { ...box.flags, [flag]: value }, version: box.version + 1 };
}

/** Adds a hold. A second active hold of the same type is not added twice. */
export function placeBoxHold(box: Box, hold: Hold): Box {
  const already = box.holds.some((h) => h.releasedAt === undefined && h.type === hold.type);
  return already ? box : { ...box, holds: [...box.holds, hold], version: box.version + 1 };
}

/** Releases every active hold of a type. */
export function releaseBoxHolds(
  box: Box,
  type: Hold['type'],
  release: { readonly by: string; readonly at: string },
): Box {
  let changed = false;
  const holds = box.holds.map((h) => {
    if (h.releasedAt !== undefined || h.type !== type) return h;
    changed = true;
    return { ...h, releasedBy: release.by, releasedAt: release.at };
  });
  return changed ? { ...box, holds, version: box.version + 1 } : box;
}
